import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

// LuzGrid CRM button primitive — MASTER_PROMPT §1.4.
//
// Variants: primary, secondary, ghost, danger, link, ai (Lumen / Generate-with-AI).
// Sizes:    sm (28 px), md (32 px default), lg (38 px).
// Loading:  spinner replaces the leading icon, button stays the same width
//           and is disabled, so action buttons don't reflow when clicked.
//
// Aliases (`default` → primary, `outline` → secondary, `destructive` → danger,
// `icon` → square-md) are kept so existing call sites continue to work
// without a sweep.
const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded font-medium",
    "transition-colors duration-[120ms] ease-lz",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg-app",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        primary:
          "bg-accent text-accent-fg hover:bg-accent-hover shadow-1",
        secondary:
          "bg-bg-card border border-border text-text hover:bg-bg-hover",
        ghost:
          "text-text-2 hover:bg-bg-hover hover:text-text",
        danger:
          "bg-danger text-white hover:bg-danger/90",
        "danger-ghost":
          "text-danger hover:bg-danger-soft",
        link:
          "text-accent hover:text-accent-hover underline-offset-2 hover:underline px-0 h-auto",
        ai:
          "bg-ai text-white hover:bg-ai/90 shadow-1",
        info:
          "bg-info-soft text-info border border-info/35 hover:bg-info/20",
        // Legacy aliases — keep working until existing call sites migrate.
        default: "bg-accent text-accent-fg hover:bg-accent-hover shadow-1",
        outline:
          "bg-bg-card border border-border text-text hover:bg-bg-hover",
        destructive: "bg-danger text-white hover:bg-danger/90",
      },
      size: {
        sm: "h-7 px-2.5 text-xs [&_svg]:size-3.5",
        md: "h-8 px-3 text-base [&_svg]:size-4",
        lg: "h-9 px-4 text-md [&_svg]:size-4",
        icon: "h-8 w-8 [&_svg]:size-4",
        // Legacy alias
        default: "h-8 px-3 text-base [&_svg]:size-4",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    if (loading && !asChild) {
      return (
        <Comp
          ref={ref}
          disabled
          className={cn(buttonVariants({ variant, size, className }))}
          {...props}
        >
          <Loader2 className="animate-spin" />
          {children}
        </Comp>
      );
    }
    return (
      <Comp
        ref={ref}
        disabled={disabled}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { buttonVariants };
